/**
 * Shared OAuth Utilities
 *
 * Common OAuth 2.0 helpers used by all frontend apps (pickem, squares, daily-reveal).
 * Handles the SSO redirect flow:
 *   1. Frontend calls backend to get OAuth authorize URL
 *   2. User authenticates with DraftKings
 *   3. DraftKings redirects to backend callback
 *   4. Backend redirects to frontend with token in URL params
 *   5. Frontend extracts token and stores it
 *
 * The iframe token flow (path 1) is handled separately in each app's AuthContext.
 */
import { getTenantBasePath } from './tenant-resolver';

// ===== State Management =====

/** Generate a random state parameter for CSRF protection */
export function generateOAuthState(): string {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Store OAuth state in sessionStorage for verification */
export function storeOAuthState(state: string): void {
  sessionStorage.setItem('oauth_state', state);
}

/** Retrieve and remove OAuth state from sessionStorage */
export function retrieveOAuthState(): string | null {
  const state = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  return state;
}

// ===== Return URL (deep-link preservation) =====

const RETURN_URL_KEY = 'oauth_return_url';

/**
 * Store the current URL (path + query) so the OAuthCallback component can
 * restore it after sign-in. Stores the path RELATIVE to the tenant base
 * path because all BrowserRouter instances use `basename={TENANT_BASE_PATH}`
 * — storing the full pathname would cause the router to double-prefix on
 * navigate(). Skips storing auth-callback URLs to avoid loops.
 */
export function storeReturnUrl(): void {
  try {
    const { pathname, search } = window.location;
    if (pathname.endsWith('/auth/callback')) return;
    const basePath = getTenantBasePath();
    const relativePath =
      basePath && pathname.startsWith(basePath) ? pathname.slice(basePath.length) || '/' : pathname;
    sessionStorage.setItem(RETURN_URL_KEY, relativePath + search);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — best-effort only
  }
}

/**
 * Retrieve and clear the stored return URL. Returns null if none set or if
 * the value would redirect back to the callback route.
 */
export function popReturnUrl(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    if (!value || value.includes('/auth/callback')) return null;
    return value;
  } catch {
    return null;
  }
}

// ===== OAuth Flow =====

/**
 * Initiate OAuth login flow
 *
 * Calls the backend to get the OAuth authorization URL, then redirects the user.
 * The X-Tenant-Slug header is required for per-tenant OAuth configuration.
 *
 * @param apiBaseUrl - Backend API URL (e.g., 'http://localhost:3001')
 * @param tenantSlug - Tenant slug for X-Tenant-Slug header
 */
export async function initiateOAuthLogin(apiBaseUrl: string, tenantSlug: string): Promise<void> {
  try {
    const state = generateOAuthState();
    storeOAuthState(state);

    // Preserve the current URL so we can return to it after sign-in.
    storeReturnUrl();

    // Include tenant base path so backend knows where to redirect after OAuth
    const frontendUrl = `${window.location.origin}${getTenantBasePath()}`;

    const authUrl = `${apiBaseUrl}/api/auth/oauth/authorize?state=${encodeURIComponent(state)}&frontend_url=${encodeURIComponent(frontendUrl)}`;

    const response = await fetch(authUrl, {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Slug': tenantSlug,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }

    const data = await response.json();

    // Redirect to OAuth provider
    window.location.href = data.authUrl;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to initiate OAuth login: ${errorMessage}`);
  }
}

// ===== Param Extraction =====

/**
 * Extract OAuth parameters from current URL
 *
 * Checks both hash params (HashRouter: /#/auth/callback?token=...)
 * and search params (BrowserRouter: /auth/callback?token=...)
 * so this works for all frontend apps regardless of router type.
 */
export function extractOAuthParams(): {
  token?: string;
  username?: string;
  sso_user_id?: string;
  error?: string;
  error_description?: string;
} {
  let params: URLSearchParams;

  // Check hash first (HashRouter: params after ? in hash)
  const hash = window.location.hash;
  if (hash && hash.includes('?')) {
    const queryString = hash.split('?')[1];
    params = new URLSearchParams(queryString);
  } else {
    // BrowserRouter: params in search string
    params = new URLSearchParams(window.location.search);
  }

  return {
    token: params.get('token') || undefined,
    username: params.get('username') || undefined,
    sso_user_id: params.get('sso_user_id') || undefined,
    error: params.get('error') || undefined,
    error_description: params.get('error_description') || undefined,
  };
}

/**
 * Check if current URL is an OAuth callback
 * Works for both HashRouter and BrowserRouter
 */
export function isOAuthCallback(): boolean {
  return (
    window.location.pathname.endsWith('/auth/callback') ||
    window.location.hash.includes('/auth/callback')
  );
}
